import { cn } from "../../lib/utils";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg";
  className?: string;
  label?: string;
}

export function LoadingSpinner({
  size = "md",
  className,
  label,
}: LoadingSpinnerProps) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3", className)}>
      <div
        className={cn(
          "relative",
          {
            "h-6 w-6": size === "sm",
            "h-10 w-10": size === "md",
            "h-16 w-16": size === "lg",
          }
        )}
      >
        <div
          className={cn(
            "absolute inset-0 rounded-full border-2 border-surface-700"
          )}
        />
        <div
          className={cn(
            "absolute inset-0 rounded-full border-2 border-transparent border-t-brand-500 animate-spin"
          )}
        />
        {size === "lg" && (
          <div
            className="absolute inset-1 rounded-full border-2 border-transparent border-b-purple-500 animate-spin"
            style={{ animationDirection: "reverse", animationDuration: "1.5s" }}
          />
        )}
      </div>
      {label && (
        <p className="text-sm text-surface-400 animate-pulse">{label}</p>
      )}
    </div>
  );
}

export function FullPageLoader({ label }: { label?: string }) {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-surface-900">
      <div className="flex flex-col items-center gap-4">
        <LoadingSpinner size="lg" label={label || "Loading..."} />
        {/* Ambient glow */}
        <div className="absolute h-32 w-32 rounded-full bg-brand-500/5 blur-3xl" />
      </div>
    </div>
  );
}

/** Skeleton placeholder for content that is loading */
export function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-lg bg-surface-700/50",
        className
      )}
      {...props}
    />
  );
}

/** Skeleton card for list/table loading states */
export function SkeletonCard() {
  return (
    <div className="rounded-xl border border-surface-700/50 bg-surface-800/80 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-8 rounded-lg" />
      </div>
      <Skeleton className="h-8 w-20" />
      <Skeleton className="h-3 w-32" />
    </div>
  );
}
